import React, { useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Tab } from '@headlessui/react';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  BookOpenIcon,
  PencilSquareIcon,
  CheckBadgeIcon,
  XMarkIcon,
  EyeIcon,
  DocumentTextIcon,
  TagIcon,
  ClockIcon,
  UserCircleIcon,
  PaperClipIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { sanitizeHtml } from '../../utils/sanitize';

interface KnowledgeAttachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  storagePath?: string;
}

interface KnowledgeActivity {
  id: string;
  articleId: string;
  activityType: string;
  previousState?: string;
  newState?: string;
  comments?: string;
  metadata?: any;
  performedById: string;
  performedAt: string;
  User: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface KnowledgeArticle {
  _id: string;
  id?: string;
  number: string;
  title?: string;
  shortDescription: string;
  content?: string;
  category?: string;
  categoryName?: string;
  knowledgeBaseId?: string;
  knowledgeBaseName?: string;
  kbBase?: {
    id: string;
    name: string;
    owner?: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  kbGroupEmail?: string;
  validTill?: string;
  tags?: string[];
  state: string;
  published: boolean;
  publishedDate?: string;
  version: number;
  views: number;
  rating: number;
  ratingCount?: number;
  assignedTo?: {
    _id: string;
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  author?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  approvedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  publishedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  retiredBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  attachments?: KnowledgeAttachment[];
  relatedArticles?: string[];
  retireReason?: string;
  canReview?: boolean;
  createdAt: string;
  updatedAt: string;
}

const stateColors: Record<string, string> = {
  new: 'bg-gray-100 text-gray-800',
  in_review: 'bg-yellow-100 text-yellow-800',
  reviewed: 'bg-blue-100 text-blue-800',
  published: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  retired: 'bg-gray-400 text-gray-800'
};

const stateLabels: Record<string, string> = {
  new: 'Draft',
  in_review: 'In Review',
  reviewed: 'Reviewed',
  published: 'Published',
  rejected: 'Rejected',
  retired: 'Retired'
};

const workflowSteps = [
  { id: 'new', name: 'Draft', icon: PencilSquareIcon },
  { id: 'in_review', name: 'In Review', icon: ClockIcon },
  { id: 'reviewed', name: 'Reviewed', icon: CheckBadgeIcon },
  { id: 'published', name: 'Published', icon: CheckBadgeIcon },
  { id: 'retired', name: 'Retired', icon: XMarkIcon }
];

const KnowledgeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDate } = useDateFormatter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'details' | 'attachments' | 'activity'>('details');
  const [previewAttachment, setPreviewAttachment] = useState<KnowledgeAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [excelHtml, setExcelHtml] = useState<string | null>(null);
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [retireReason, setRetireReason] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState<'reviewed' | 'rejected'>('reviewed');
  const [reviewComments, setReviewComments] = useState('');

  const { data: article, isLoading, error } = useQuery<KnowledgeArticle>({
    queryKey: ['knowledge-article', id],
    queryFn: async () => {
      const response = await api.get(`/knowledge/${id}`);
      return response.data;
    }
  });

  // Fetch activities for the article
  const { data: activitiesData } = useQuery<{ activities: KnowledgeActivity[] }>({
    queryKey: ['knowledge-activities', id],
    queryFn: async () => {
      const response = await api.get(`/knowledge/${id}/activities`);
      return response.data;
    },
    enabled: !!id
  });

  const activities = activitiesData?.activities || [];

  const updateStateMutation = useMutation({
    mutationFn: async (newState: string) => {
      const response = await api.patch(`/knowledge/${id}`, { state: newState });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
    }
  });

  // Send article for review - uses dedicated endpoint with email notification
  const sendForReviewMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/knowledge/${id}/send-for-review`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      toast.success(data.message || 'Article sent for review successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send article for review');
    }
  });

  // Review article - KB owner or DL members can approve or reject
  const reviewMutation = useMutation({
    mutationFn: async ({ action, comments }: { action: 'reviewed' | 'rejected'; comments: string }) => {
      const response = await api.post(`/knowledge/${id}/review`, { action, comments });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-activities', id] });
      toast.success(data.message || 'Article reviewed successfully');
      setShowReviewModal(false);
      setReviewComments('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to review article');
    }
  });

  // Publish article - KB owner only
  const publishMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/knowledge/${id}/publish`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      toast.success(data.message || 'Article published successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to publish article');
    }
  });

  // Retire article - can be done from any state
  const retireMutation = useMutation({
    mutationFn: async (reason: string) => {
      const response = await api.post(`/knowledge/${id}/retire`, { reason });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-activities', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge'] });
      toast.success(data.message || 'Article retired successfully');
      setShowRetireModal(false);
      setRetireReason('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to retire article');
    }
  });

  // Reset retired article to draft
  const resetToDraftMutation = useMutation({
    mutationFn: async (reason: string = '') => {
      const response = await api.post(`/knowledge/${id}/reset-to-draft`, { reason });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-article', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-activities', id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge'] });
      toast.success(data.message || 'Article reset to draft successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reset article to draft');
    }
  });

  const isDocxFile = (mimeType: string) => {
    return mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
           mimeType === 'application/msword';
  };

  const isExcelFile = (mimeType: string) => {
    return mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
           mimeType === 'application/vnd.ms-excel' ||
           mimeType === 'application/x-excel';
  };

  const handlePreview = async (attachment: KnowledgeAttachment) => {
    setPreviewAttachment(attachment);
    setPreviewLoading(true);
    setDocxHtml(null);
    setExcelHtml(null);
    try {
      const response = await api.get(
        `/knowledge/${id}/attachments/${attachment.id}/download`,
        { responseType: 'arraybuffer' }
      );

      // Handle DOCX files with mammoth
      if (isDocxFile(attachment.mimeType)) {
        // Style map to preserve document formatting
        const styleMap = [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
          "p[style-name='Heading 4'] => h4:fresh",
          "p[style-name='Heading 5'] => h5:fresh",
          "p[style-name='Heading 6'] => h6:fresh",
          "p[style-name='Title'] => h1.document-title:fresh",
          "p[style-name='Subtitle'] => h2.document-subtitle:fresh",
          "p[style-name='TOC Heading'] => h2.toc-heading:fresh",
          "p[style-name='TOC 1'] => p.toc-entry.toc-level-1:fresh",
          "p[style-name='TOC 2'] => p.toc-entry.toc-level-2:fresh",
          "p[style-name='TOC 3'] => p.toc-entry.toc-level-3:fresh",
          "p[style-name='TOC 4'] => p.toc-entry.toc-level-4:fresh",
          "p[style-name='List Paragraph'] => p.list-paragraph:fresh",
          "p[style-name='Quote'] => blockquote:fresh",
          "p[style-name='Intense Quote'] => blockquote.intense-quote:fresh",
          "r[style-name='Strong'] => strong",
          "r[style-name='Emphasis'] => em",
          "r[style-name='Intense Emphasis'] => em.intense",
          "r[style-name='Subtle Reference'] => span.subtle-reference",
          "r[style-name='Book Title'] => cite",
          // Handle table styling
          "table => table.docx-table",
          // Preserve line breaks
          "br[type='page'] => hr.page-break",
          "br[type='column'] => br.column-break",
        ];

        const result = await mammoth.convertToHtml({
          arrayBuffer: response.data
        }, {
          styleMap: styleMap,
          convertImage: mammoth.images.imgElement((image) => {
            return image.read("base64").then((imageBuffer) => {
              return {
                src: `data:${image.contentType};base64,${imageBuffer}`
              };
            });
          })
        });
        setDocxHtml(result.value);
        // Also create a blob URL for download
        const blob = new Blob([response.data], { type: attachment.mimeType });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } else if (isExcelFile(attachment.mimeType)) {
        // Handle Excel files with SheetJS
        const workbook = XLSX.read(response.data, { type: 'array' });
        let htmlContent = '<div class="excel-preview">';

        // Convert all sheets to HTML
        workbook.SheetNames.forEach((sheetName, index) => {
          const worksheet = workbook.Sheets[sheetName];
          const sheetHtml = XLSX.utils.sheet_to_html(worksheet, { editable: false });
          htmlContent += `
            <div class="sheet-container ${index > 0 ? 'mt-6' : ''}">
              <h3 class="text-lg font-semibold text-gray-700 mb-2 pb-2 border-b">${sheetName}</h3>
              <div class="overflow-x-auto">${sheetHtml}</div>
            </div>
          `;
        });

        htmlContent += '</div>';
        setExcelHtml(htmlContent);

        // Also create a blob URL for download
        const blob = new Blob([response.data], { type: attachment.mimeType });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } else {
        // Handle other file types
        const blob = new Blob([response.data], { type: attachment.mimeType });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewAttachment(null);
    setPreviewUrl(null);
    setDocxHtml(null);
    setExcelHtml(null);
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes || isNaN(bytes)) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getWorkflowStatus = (state: string) => {
    const stateIndex = workflowSteps.findIndex(step => step.id === state);
    return stateIndex;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading article. Please try again.</p>
        </div>
      </div>
    );
  }

  const currentStepIndex = getWorkflowStatus(article.state);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/knowledge')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" />
          Back to Knowledge Base
        </button>
        <div className="flex items-center space-x-3">
          {article.state !== 'published' && article.state !== 'retired' && (
            <button
              onClick={() => navigate(`/knowledge/${id}/edit`)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PencilSquareIcon className="h-4 w-4 mr-2" />
              Edit Article
            </button>
          )}
          {article.state !== 'retired' && (
            <button
              onClick={() => setShowRetireModal(true)}
              disabled={retireMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              {retireMutation.isPending ? 'Retiring...' : 'Retire Article'}
            </button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{article.title || article.shortDescription}</h1>
              <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                <span className="flex items-center">
                  <BookOpenIcon className="h-4 w-4 mr-1" />
                  {article.number}
                </span>
                <span className="flex items-center">
                  <EyeIcon className="h-4 w-4 mr-1" />
                  {article.views} views
                </span>
                <span className="flex items-center">
                  <DocumentTextIcon className="h-4 w-4 mr-1" />
                  Version {article.version}
                </span>
              </div>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stateColors[article.state as keyof typeof stateColors]}`}>
              {stateLabels[article.state as keyof typeof stateLabels]}
            </span>
          </div>

        </div>

        {/* Workflow Progress */}
        <div className="px-6 py-4">
          <nav aria-label="Progress">
            <ol className="flex items-center">
              {workflowSteps.map((step, stepIdx) => {
                const Icon = step.icon;
                const isActive = stepIdx === currentStepIndex;
                const isCompleted = stepIdx < currentStepIndex;
                
                return (
                  <li key={step.id} className={stepIdx !== workflowSteps.length - 1 ? 'flex-1' : ''}>
                    <div className="flex items-center">
                      <div className={`
                        flex items-center justify-center w-10 h-10 rounded-full
                        ${isCompleted ? 'bg-indigo-600' : isActive ? 'bg-indigo-600' : 'bg-gray-300'}
                      `}>
                        <Icon className={`w-6 h-6 ${isCompleted || isActive ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      {stepIdx !== workflowSteps.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-indigo-600' : 'bg-gray-300'}`} />
                      )}
                    </div>
                    <p className={`mt-2 text-xs font-medium ${isActive ? 'text-indigo-600' : isCompleted ? 'text-gray-900' : 'text-gray-500'}`}>
                      {step.name}
                    </p>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>

        {/* Quick Actions */}
        {!article.published && article.state === 'new' && (() => {
          const isKbOwner = user && article?.kbBase?.owner?.id === (user as any).id;
          return (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {isKbOwner ? 'Ready to publish this article?' : 'Ready to submit this article for review?'}
                </p>
                <div className="flex items-center space-x-3">
                  {!isKbOwner && (
                    <button
                      onClick={() => sendForReviewMutation.mutate()}
                      disabled={sendForReviewMutation.isPending}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {sendForReviewMutation.isPending ? 'Sending...' : 'Send for Review'}
                    </button>
                  )}
                  {isKbOwner && (
                    <>
                      <button
                        onClick={() => sendForReviewMutation.mutate()}
                        disabled={sendForReviewMutation.isPending}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        {sendForReviewMutation.isPending ? 'Sending...' : 'Send for Review'}
                      </button>
                      <button
                        onClick={() => publishMutation.mutate()}
                        disabled={publishMutation.isPending}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <CheckBadgeIcon className="h-4 w-4 mr-2" />
                        {publishMutation.isPending ? 'Publishing...' : 'Publish Now'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Review Actions */}
        {article.state === 'in_review' && (
          <div className="px-6 py-4 bg-yellow-50 border-t border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Article Under Review
                </p>
                <p className="text-sm text-yellow-600">
                  {article.canReview
                    ? 'This article is waiting for your approval before publication.'
                    : 'This article has been submitted for review by the knowledge base owner or group members.'}
                </p>
              </div>
              {article.canReview && (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setReviewAction('rejected');
                      setShowReviewModal(true);
                    }}
                    disabled={reviewMutation.isPending}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    <XMarkIcon className="h-4 w-4 mr-2" />
                    Reject
                  </button>
                  <button
                    onClick={() => {
                      setReviewAction('reviewed');
                      setShowReviewModal(true);
                    }}
                    disabled={reviewMutation.isPending}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckBadgeIcon className="h-4 w-4 mr-2" />
                    Approve
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reviewed Article Actions - Ready for publish */}
        {article.state === 'reviewed' && (
          <div className="px-6 py-4 bg-blue-50 border-t border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Article Reviewed
                </p>
                <p className="text-sm text-blue-600">
                  This article has been approved and is ready for publication.
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => publishMutation.mutate()}
                  disabled={publishMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckBadgeIcon className="h-4 w-4 mr-2" />
                  {publishMutation.isPending ? 'Publishing...' : 'Publish Now'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Published Article Actions */}
        {article.state === 'published' && (
          <div className="px-6 py-4 bg-green-50 border-t border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">
                  Article Published
                </p>
                <p className="text-sm text-green-600">
                  This article is live and visible to all users.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Retired Article Actions */}
        {article.state === 'retired' && (
          <div className="px-6 py-4 bg-red-50 border-t border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">
                  Article Retired
                </p>
                <p className="text-sm text-red-600">
                  This article has been retired and is no longer visible to users.
                </p>
                {article.retireReason && (
                  <div className="mt-2 p-2 bg-red-100 rounded-md">
                    <p className="text-xs font-medium text-red-700">Retire Reason:</p>
                    <p className="text-sm text-red-800">{article.retireReason}</p>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => resetToDraftMutation.mutate('')}
                  disabled={resetToDraftMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  {resetToDraftMutation.isPending ? 'Resetting...' : 'Reset to Draft'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm rounded-t-lg border border-gray-200">
        <nav className="flex border-b border-gray-200" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('details')}
            className={`${
              activeTab === 'details'
                ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
            } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('attachments')}
            className={`${
              activeTab === 'attachments'
                ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
            } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px flex items-center`}
          >
            Attachments
            {article.attachments && article.attachments.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {article.attachments.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`${
              activeTab === 'activity'
                ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
            } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px flex items-center`}
          >
            Activity
            {activities.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {activities.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="bg-white shadow-sm rounded-b-lg border border-t-0 border-gray-200">
          <div className="p-6">
            {/* Article Details Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Number</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{article.number}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</dt>
                <dd className="mt-1 text-sm text-gray-900">{article.categoryName || article.category || '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">State</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateColors[article.state as keyof typeof stateColors]}`}>
                    {stateLabels[article.state as keyof typeof stateLabels]}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Version</dt>
                <dd className="mt-1 text-sm text-gray-900">{article.version}</dd>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Knowledge Base</dt>
                <dd className="mt-1 text-sm text-gray-900">{article.kbBase?.name || article.knowledgeBaseName || '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">KB Group Email</dt>
                <dd className="mt-1 text-sm text-gray-900">{article.kbGroupEmail || '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Author</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {article.assignedTo
                    ? `${article.assignedTo.firstName || ''} ${article.assignedTo.lastName || ''}`.trim() || article.assignedTo.email
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Valid Till</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {article.validTill ? formatDate(article.validTill) : '-'}
                </dd>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(article.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(article.updatedAt)}</dd>
              </div>
              {article.approvedBy && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Approved By</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {article.approvedBy.firstName} {article.approvedBy.lastName}
                  </dd>
                </div>
              )}
              {article.publishedBy && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Published By</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {article.publishedBy.firstName} {article.publishedBy.lastName}
                  </dd>
                </div>
              )}
              {article.retiredBy && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Retired By</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {article.retiredBy.firstName} {article.retiredBy.lastName}
                  </dd>
                </div>
              )}
            </div>

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mb-6">
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Tags</dt>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Short Description */}
            <div className="mb-6">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Short Description</dt>
              <dd className="text-sm text-gray-900">{article.shortDescription || '-'}</dd>
            </div>

            {/* Article Content */}
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Content</dt>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50" style={{ minHeight: '200px' }}>
                <div
                  className="prose prose-indigo max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.content || '') }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'attachments' && (
        <div className="bg-white shadow-sm rounded-b-lg border border-t-0 border-gray-200">
          <div className="p-6">
            {(!article.attachments || article.attachments.length === 0) ? (
              <div className="text-center py-12">
                <PaperClipIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No attachments</h3>
                <p className="mt-1 text-sm text-gray-500">This article has no attached files.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {article.attachments.map((attachment) => (
                  <li key={attachment.id} className="py-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400 mr-4" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.originalName}</p>
                        <p className="text-xs text-gray-500">
                          {attachment.mimeType} • {formatFileSize(attachment.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handlePreview(attachment)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Preview
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="bg-white shadow-sm rounded-b-lg border border-t-0 border-gray-200">
          <div className="p-6">
            {activities.length === 0 ? (
              <div className="text-center py-12">
                <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No activity</h3>
                <p className="mt-1 text-sm text-gray-500">No activity has been recorded for this article yet.</p>
              </div>
            ) : (
              <div className="flow-root">
                <ul className="-mb-8">
                  {activities.map((activity, activityIdx) => {
                    const activityTypeLabels: Record<string, string> = {
                      'SENT_FOR_REVIEW': 'Sent for Review',
                      'APPROVED': 'Approved',
                      'REJECTED': 'Rejected',
                      'PUBLISHED': 'Published',
                      'RETIRED': 'Retired',
                      'STATE_CHANGE': 'State Changed',
                      'UPDATED': 'Article Updated',
                      'RESET_TO_DRAFT': 'Reset to Draft'
                    };
                    const activityTypeColors: Record<string, string> = {
                      'SENT_FOR_REVIEW': 'bg-yellow-500',
                      'APPROVED': 'bg-green-500',
                      'REJECTED': 'bg-red-500',
                      'PUBLISHED': 'bg-indigo-500',
                      'RETIRED': 'bg-gray-500',
                      'STATE_CHANGE': 'bg-blue-500',
                      'UPDATED': 'bg-cyan-500',
                      'RESET_TO_DRAFT': 'bg-orange-500'
                    };
                    return (
                      <li key={activity.id}>
                        <div className="relative pb-8">
                          {activityIdx !== activities.length - 1 && (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          )}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${activityTypeColors[activity.activityType] || 'bg-gray-400'}`}>
                                {activity.activityType === 'APPROVED' && <CheckBadgeIcon className="h-4 w-4 text-white" />}
                                {activity.activityType === 'REJECTED' && <XMarkIcon className="h-4 w-4 text-white" />}
                                {activity.activityType === 'SENT_FOR_REVIEW' && <ClockIcon className="h-4 w-4 text-white" />}
                                {activity.activityType === 'PUBLISHED' && <BookOpenIcon className="h-4 w-4 text-white" />}
                                {activity.activityType === 'RETIRED' && <XMarkIcon className="h-4 w-4 text-white" />}
                                {activity.activityType === 'UPDATED' && <PencilSquareIcon className="h-4 w-4 text-white" />}
                                {activity.activityType === 'RESET_TO_DRAFT' && <ArrowPathIcon className="h-4 w-4 text-white" />}
                                {!['APPROVED', 'REJECTED', 'SENT_FOR_REVIEW', 'PUBLISHED', 'RETIRED', 'UPDATED', 'RESET_TO_DRAFT'].includes(activity.activityType) && (
                                  <ChatBubbleLeftRightIcon className="h-4 w-4 text-white" />
                                )}
                              </span>
                            </div>
                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                              <div>
                                <p className="text-sm text-gray-900">
                                  <span className="font-medium">
                                    {activity.User?.firstName} {activity.User?.lastName}
                                  </span>{' '}
                                  <span className="text-gray-600">
                                    {activityTypeLabels[activity.activityType] || activity.activityType}
                                  </span>
                                </p>
                                {activity.comments && (
                                  <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                                    <p className="text-xs font-medium text-gray-500 mb-1">Comments:</p>
                                    <p className="text-sm text-gray-700">{activity.comments}</p>
                                  </div>
                                )}
                                {activity.previousState && activity.newState && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    State: {stateLabels[activity.previousState] || activity.previousState} → {stateLabels[activity.newState] || activity.newState}
                                  </p>
                                )}
                              </div>
                              <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                <time dateTime={activity.performedAt}>
                                  {formatDate(activity.performedAt)}
                                </time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewAttachment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closePreview}></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {previewAttachment.originalName}
                </h3>
                <button
                  onClick={closePreview}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="border rounded-lg overflow-hidden bg-gray-50 min-h-[400px] flex items-center justify-center">
                {previewLoading ? (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
                    <p className="mt-4 text-gray-500">Loading preview...</p>
                  </div>
                ) : docxHtml ? (
                  // DOCX preview rendered as HTML with custom styling
                  <div className="w-full h-[600px] overflow-auto bg-white">
                    <style>{`
                      .docx-preview h1 { font-size: 2rem; font-weight: 700; margin: 1.5rem 0 1rem; color: #1f2937; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.5rem; }
                      .docx-preview h2 { font-size: 1.5rem; font-weight: 600; margin: 1.25rem 0 0.75rem; color: #374151; }
                      .docx-preview h3 { font-size: 1.25rem; font-weight: 600; margin: 1rem 0 0.5rem; color: #4b5563; }
                      .docx-preview h4 { font-size: 1.125rem; font-weight: 600; margin: 0.875rem 0 0.5rem; color: #6b7280; }
                      .docx-preview h5 { font-size: 1rem; font-weight: 600; margin: 0.75rem 0 0.375rem; color: #6b7280; }
                      .docx-preview h6 { font-size: 0.875rem; font-weight: 600; margin: 0.5rem 0 0.25rem; color: #9ca3af; }
                      .docx-preview p { margin: 0.5rem 0; line-height: 1.6; color: #374151; }
                      .docx-preview .document-title { font-size: 2.5rem; text-align: center; margin-bottom: 0.5rem; border-bottom: none; }
                      .docx-preview .document-subtitle { font-size: 1.25rem; text-align: center; color: #6b7280; margin-top: 0; font-weight: 400; }
                      .docx-preview .toc-heading { font-size: 1.5rem; font-weight: 700; margin: 1.5rem 0 1rem; color: #1f2937; border-bottom: 2px solid #3b82f6; padding-bottom: 0.5rem; }
                      .docx-preview .toc-entry { margin: 0.375rem 0; color: #2563eb; cursor: default; }
                      .docx-preview .toc-level-1 { padding-left: 0; font-weight: 500; }
                      .docx-preview .toc-level-2 { padding-left: 1.5rem; }
                      .docx-preview .toc-level-3 { padding-left: 3rem; }
                      .docx-preview .toc-level-4 { padding-left: 4.5rem; font-size: 0.875rem; }
                      .docx-preview ul, .docx-preview ol { padding-left: 1.5rem; margin: 0.5rem 0; }
                      .docx-preview li { margin: 0.25rem 0; line-height: 1.6; }
                      .docx-preview ul { list-style-type: disc; }
                      .docx-preview ol { list-style-type: decimal; }
                      .docx-preview blockquote { border-left: 4px solid #3b82f6; padding-left: 1rem; margin: 1rem 0; font-style: italic; color: #6b7280; background: #f8fafc; padding: 1rem; }
                      .docx-preview .intense-quote { border-left-color: #1d4ed8; background: #eff6ff; font-weight: 500; }
                      .docx-preview table.docx-table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
                      .docx-preview table.docx-table th, .docx-preview table.docx-table td { border: 1px solid #e5e7eb; padding: 0.5rem 0.75rem; text-align: left; }
                      .docx-preview table.docx-table th { background: #f3f4f6; font-weight: 600; }
                      .docx-preview table.docx-table tr:nth-child(even) { background: #f9fafb; }
                      .docx-preview img { max-width: 100%; height: auto; margin: 1rem 0; border-radius: 0.375rem; }
                      .docx-preview hr.page-break { border: none; border-top: 2px dashed #d1d5db; margin: 2rem 0; }
                      .docx-preview strong, .docx-preview b { font-weight: 700; }
                      .docx-preview em, .docx-preview i { font-style: italic; }
                      .docx-preview em.intense { font-weight: 500; color: #1f2937; }
                      .docx-preview a { color: #2563eb; text-decoration: underline; }
                      .docx-preview .list-paragraph { padding-left: 1rem; }
                    `}</style>
                    <div
                      className="docx-preview p-6"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(docxHtml) }}
                    />
                  </div>
                ) : excelHtml ? (
                  // Excel preview rendered as HTML table
                  <div
                    className="w-full h-[600px] overflow-auto p-4 bg-white"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(excelHtml) }}
                    style={{
                      // Style for Excel table
                    }}
                  />
                ) : previewUrl ? (
                  previewAttachment.mimeType.startsWith('image/') ? (
                    <img
                      src={previewUrl}
                      alt={previewAttachment.originalName}
                      className="max-w-full max-h-[600px] object-contain"
                    />
                  ) : previewAttachment.mimeType === 'application/pdf' ? (
                    <iframe
                      src={previewUrl}
                      className="w-full h-[600px]"
                      title={previewAttachment.originalName}
                    />
                  ) : previewAttachment.mimeType.startsWith('text/') ? (
                    <iframe
                      src={previewUrl}
                      className="w-full h-[600px]"
                      title={previewAttachment.originalName}
                    />
                  ) : (
                    <div className="text-center p-8">
                      <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto" />
                      <p className="mt-4 text-gray-600">Preview not available for this file type</p>
                      <p className="text-sm text-gray-500">{previewAttachment.mimeType}</p>
                      <a
                        href={previewUrl}
                        download={previewAttachment.originalName}
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        Download File
                      </a>
                    </div>
                  )
                ) : (
                  <div className="text-center p-8">
                    <p className="text-gray-500">Failed to load preview</p>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end space-x-3">
                {previewUrl && (
                  <a
                    href={previewUrl}
                    download={previewAttachment.originalName}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Download
                  </a>
                )}
                <button
                  onClick={closePreview}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Retire Confirmation Modal */}
      <Transition appear show={showRetireModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowRetireModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="mt-3 text-center">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Retire Article
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to retire this article? This will make it unavailable to users. You can restore it to draft later if needed.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label htmlFor="retire-reason" className="block text-sm font-medium text-gray-700 text-left">
                      Retire Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="retire-reason"
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                      placeholder="Please provide a reason for retiring this article..."
                      value={retireReason}
                      onChange={(e) => setRetireReason(e.target.value)}
                    />
                    {retireReason.trim() === '' && (
                      <p className="mt-1 text-xs text-red-500">Retire reason is required</p>
                    )}
                  </div>
                  <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-3">
                    <button
                      type="button"
                      disabled={retireMutation.isPending || retireReason.trim() === ''}
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        if (retireReason.trim()) {
                          retireMutation.mutate(retireReason);
                        }
                      }}
                    >
                      {retireMutation.isPending ? 'Retiring...' : 'Retire'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={() => setShowRetireModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Review Modal with Comments */}
      <Transition appear show={showReviewModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowReviewModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center">
                    <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${reviewAction === 'reviewed' ? 'bg-green-100' : 'bg-red-100'}`}>
                      {reviewAction === 'reviewed' ? (
                        <CheckBadgeIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                      ) : (
                        <XMarkIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      )}
                    </div>
                  </div>
                  <div className="mt-3 text-center">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {reviewAction === 'reviewed' ? 'Approve Article' : 'Reject Article'}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {reviewAction === 'reviewed'
                          ? 'Please provide comments for your approval. These will be visible in the activity log.'
                          : 'Please provide a reason for rejection. This will help the author improve the article.'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label htmlFor="review-comments" className="block text-sm font-medium text-gray-700 text-left">
                      Comments <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="review-comments"
                      rows={4}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm ${reviewAction === 'reviewed' ? 'focus:border-green-500 focus:ring-green-500' : 'focus:border-red-500 focus:ring-red-500'}`}
                      placeholder={reviewAction === 'reviewed' ? 'Enter approval comments...' : 'Enter rejection reason...'}
                      value={reviewComments}
                      onChange={(e) => setReviewComments(e.target.value)}
                    />
                    {reviewComments.trim() === '' && (
                      <p className="mt-1 text-xs text-red-500">Comments are required</p>
                    )}
                  </div>
                  <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-3">
                    <button
                      type="button"
                      disabled={reviewMutation.isPending || reviewComments.trim() === ''}
                      className={`inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                        reviewAction === 'reviewed'
                          ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                          : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      }`}
                      onClick={() => {
                        if (reviewComments.trim()) {
                          reviewMutation.mutate({ action: reviewAction, comments: reviewComments });
                        }
                      }}
                    >
                      {reviewMutation.isPending ? 'Processing...' : reviewAction === 'reviewed' ? 'Approve' : 'Reject'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={() => {
                        setShowReviewModal(false);
                        setReviewComments('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default KnowledgeDetail;
